import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Button, Form, Spinner } from 'react-bootstrap';
import { FaTshirt, FaInfoCircle } from 'react-icons/fa';
import ImageZoomModal from './ImageZoomModal';

// Normalizar API_URL (remover barra final si existe)
const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:4004').replace(/\/$/, '');

function buildInstructivoUrl(path) {
  if (!path) return null;
  // Los instructivos ahora se sirven desde el backend
  // Si el backend manda "administracion/camisa_invierno_f.jpg"
  // esto arma "http://localhost:4004/assets/tallesInstructivos/administracion/camisa_invierno_f.jpg"
  return `${API_URL}/assets/tallesInstructivos/${path}`.replace(/([^:]\/)\/+/g, '$1');
}

const TalleModalButton = ({ user }) => {
    // Estado para zoom de imagen
    const [zoomImgSrc, setZoomImgSrc] = useState(null);
  const [show, setShow] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // modal instructivo
  const [showInstructivo, setShowInstructivo] = useState(false);
  const [instructivoImgs, setInstructivoImgs] = useState([]);
  const [instructivoTitle, setInstructivoTitle] = useState('');

  const canSave = useMemo(() => !loading && !saving, [loading, saving]);

  const cargarFormulario = async () => {
    setLoading(true);
    try {
      const storedUser = localStorage.getItem('user');
      const currentUser = storedUser ? JSON.parse(storedUser) : user;
      const token = localStorage.getItem('token');
      
      const res = await fetch(`${API_URL}/api/indumentaria/form/${user.Legajo}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'x-user-id': String(currentUser?.Legajo || currentUser?.CdEmpleado || user?.Legajo || ''),
          'x-user-role': String(currentUser?.IdRol || currentUser?.tipoUsuario || '1')
        }
      });
      const data = await res.json();

      if (!data?.success) {
        console.error('❌ Error form:', data?.error);
        setItems([]);
        return;
      }

      // Esperado: data.items = [{ rol_prenda_id, label, required, talle, instructivos:{f,m,u} o instructivo_f/m/u }]
      const safeItems = Array.isArray(data.items) ? data.items : [];
      setItems(
        safeItems.map((i) => ({
          ...i,
          talle: i.talle ?? '', // para inputs controlados
        }))
      );
    } catch (err) {
      console.error('❌ Error al cargar formulario:', err);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = async () => {
    setShow(true);
    await cargarFormulario();
  };

  const handleClose = () => {
    setShow(false);
    setShowInstructivo(false);
    setInstructivoImgs([]);
    setInstructivoTitle('');
  };

  const handleChange = (rol_prenda_id, value) => {
    setItems((prev) =>
      prev.map((i) => (i.rol_prenda_id === rol_prenda_id ? { ...i, talle: value } : i))
    );
  };

  const abrirInstructivo = (item) => {
    // soporta 2 formatos:
    // 1) item.instructivos = { f, m, u }
    // 2) item.instructivo_f / item.instructivo_m / item.instructivo_u
    const f = item?.instructivos?.f ?? item?.instructivo_f ?? null;
    const m = item?.instructivos?.m ?? item?.instructivo_m ?? null;
    const u = item?.instructivos?.u ?? item?.instructivo_u ?? null;

    const imgs = [buildInstructivoUrl(f), buildInstructivoUrl(m), buildInstructivoUrl(u)].filter(Boolean);

    setInstructivoTitle(item?.label || 'Instructivo de talles');
    setInstructivoImgs(imgs);
    setShowInstructivo(true);
  };

  const handleGuardar = async () => {
    setSaving(true);
    try {
      const storedUser = localStorage.getItem('user');
      const currentUser = storedUser ? JSON.parse(storedUser) : user;
      const token = localStorage.getItem('token');
      
      const payload = {
        items: items.map(({ rol_prenda_id, talle }) => ({
          rol_prenda_id,
          talle: String(talle || '').trim() || null,
        })),
      };
      console.log('🟢 Payload enviado al backend:', payload);

      const res = await fetch(`${API_URL}/api/indumentaria/talles/${user.Legajo}`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'x-user-id': String(currentUser?.Legajo || currentUser?.CdEmpleado || user?.Legajo || ''),
          'x-user-role': String(currentUser?.IdRol || currentUser?.tipoUsuario || '1')
        },
        body: JSON.stringify(payload),
      });

      let data;
      try {
        data = await res.json();
      } catch (e) {
        console.error('❌ Error parseando respuesta JSON:', e);
        data = null;
      }
      console.log('🟢 Respuesta backend:', data);
      if (!data?.success) {
        alert('❌ Error: ' + (data?.error || 'No se pudo guardar'));
        return;
      }

      alert('✅ Talles guardados correctamente');
      setShow(false);
    } catch (err) {
      console.error('❌ Error al guardar:', err);
      alert('❌ Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <button className="bubble-button talle-bubble" onClick={handleOpen} type="button">
        <FaTshirt size={20} color="rgba(255, 255, 255, 1)" />
      </button>

      <Modal show={show} onHide={handleClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>Cargar talles de indumentaria</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <Spinner animation="border" />
            </div>
          ) : (
            <Form>
              {items.length === 0 ? (
                <div style={{ color: '#666' }}>No hay prendas configuradas para este rol.</div>
              ) : (
                items.map((item) => {
                  const tieneInstructivo =
                    !!(item?.instructivos?.f || item?.instructivos?.m || item?.instructivos?.u ||
                       item?.instructivo_f || item?.instructivo_m || item?.instructivo_u);

                  return (
                    <Form.Group className="mb-3" key={item.rol_prenda_id}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
                        <Form.Label style={{ marginBottom: 0 }}>
                          {item.label}
                          {item.required ? <span style={{ color: 'red' }}> *</span> : null}
                        </Form.Label>

                        {tieneInstructivo ? (
                          <button
                            type="button"
                            onClick={() => abrirInstructivo(item)}
                            style={{
                              border: 'none',
                              background: 'linear-gradient(135deg, #4fc3f7 60%, #1976d2 100%)',
                              color: '#fff',
                              borderRadius: '50%',
                              width: 40,
                              height: 40,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              boxShadow: '0 2px 8px rgba(33, 150, 243, 0.15)',
                              cursor: 'pointer',
                              fontSize: 22,
                              transition: 'transform 0.15s',
                              outline: 'none',
                              marginLeft: 8
                            }}
                            aria-label="Ver instructivo"
                          >
                            <FaInfoCircle />
                          </button>
                        ) : null}
                      </div>

                      <Form.Control
                        type="text"
                        value={item.talle}
                        placeholder="Ingresá el talle"
                        onChange={(e) => handleChange(item.rol_prenda_id, e.target.value)}
                      />
                    </Form.Group>
                  );
                })
              )}
            </Form>
          )}

          {/* Modal instructivo */}
          <Modal show={showInstructivo} onHide={() => setShowInstructivo(false)} centered>
            <Modal.Header closeButton>
              <Modal.Title>{instructivoTitle}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {instructivoImgs.length > 0 ? (
                instructivoImgs.map((src, idx) => (
                  <img
                    key={idx}
                    src={src}
                    alt="Instructivo"
                    style={{ width: '100%', marginBottom: 8, cursor: 'zoom-in', borderRadius: 6, boxShadow: '0 2px 8px rgba(0,0,0,0.10)' }}
                    onClick={() => setZoomImgSrc(src)}
                  />
                ))
              ) : (
                <div>No hay instructivo disponible.</div>
              )}
                    {/* Modal de zoom para imagen instructivo */}
                    <ImageZoomModal
                      show={!!zoomImgSrc}
                      src={zoomImgSrc}
                      alt="Instructivo zoom"
                      onHide={() => setZoomImgSrc(null)}
                    />
            </Modal.Body>
          </Modal>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose} disabled={saving}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleGuardar} disabled={!canSave}>
            {saving ? 'Guardando...' : 'Guardar'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default TalleModalButton;
